import { useCallback, useEffect } from "react";
import { FormProvider, useForm } from "react-hook-form";

import Button from "ds/components/Button";
import FormField from "ds/components/Form/Field";
import FullScreenModalBody from "ds/components/FullScreenModal/Body";
import FullScreenModalFooter from "ds/components/FullScreenModal/Footer";
import Input from "ds/components/Input";
import { VcsProvider } from "types/generated";

import VCSIntegrationsDocumentationButton from "../../components/DocumentationButton";
import GitCheckoutToggle from "../../components/GitCheckoutToggle";
import VCSChecksToggle from "../../components/VCSChecksToggle";
import { ORIGIN_INTEGRATION_NAME } from "../../constants";
import useCloseFullScreenModal from "../../useCloseFullScreenModal";
import { UpdateOriginIntegrationData } from "./gql";
import { getFormDefaultValues, mapUpdateOriginIntegrationInputValues } from "./helpers";
import { ManageOriginManualFormFields } from "./types";
import useUpdateOriginIntegration from "./useUpdateOriginIntegration";

type VCSIntegrationsManageOriginManualProps = {
  integration?: UpdateOriginIntegrationData;
  onIsDirtyChange: (value: boolean) => void;
};

const VCSIntegrationsManageOriginManual = ({
  integration,
  onIsDirtyChange,
}: VCSIntegrationsManageOriginManualProps) => {
  const { handleOnClose } = useCloseFullScreenModal();

  const manageOriginManualForm = useForm<ManageOriginManualFormFields>({
    defaultValues: getFormDefaultValues(integration),
    mode: "onChange",
  });

  const {
    formState: { isDirty },
    handleSubmit,
  } = manageOriginManualForm;

  const { updateOriginIntegration, loading: updateLoading } = useUpdateOriginIntegration();

  const handleSetup = useCallback(
    () =>
      handleSubmit((formData: ManageOriginManualFormFields) => {
        updateOriginIntegration(mapUpdateOriginIntegrationInputValues(formData), handleOnClose);
      })(),
    [handleOnClose, updateOriginIntegration, handleSubmit]
  );

  useEffect(() => {
    onIsDirtyChange(isDirty);
  }, [isDirty, onIsDirtyChange]);

  return (
    <FormProvider {...manageOriginManualForm}>
      <FullScreenModalBody>
        <FormField
          label="Integration name"
          helperText="The integration name is unique and cannot be changed"
          noMargin
        >
          {({ ariaInputProps }) => (
            <Input value={ORIGIN_INTEGRATION_NAME} disabled {...ariaInputProps} />
          )}
        </FormField>

        <GitCheckoutToggle disabled={true} />
        <VCSChecksToggle />
      </FullScreenModalBody>
      <FullScreenModalFooter justify="between">
        <VCSIntegrationsDocumentationButton provider={VcsProvider.Origin} />

        <Button onPress={handleSetup} variant="primary" loading={updateLoading}>
          Save
        </Button>
      </FullScreenModalFooter>
    </FormProvider>
  );
};

export default VCSIntegrationsManageOriginManual;
